import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Plug, Trash2, Pencil, PlayCircle } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  listMcpConnections,
  saveMcpConnection,
  deleteMcpConnection,
  testMcpConnection,
  setMcpAllowedTools,
} from "@/lib/mcp-connections.functions";

export const Route = createFileRoute("/_authenticated/admin/mcp-connections")({
  head: () => ({
    meta: [
      { title: "Conexiones MCP — Panel Mostrando México" },
      { name: "description", content: "Configura servidores MCP remotos con dirección y token." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Page,
});

type Form = { id?: string; name: string; url: string; token: string; enabled: boolean };
const empty: Form = { name: "", url: "", token: "", enabled: true };

function Page() {
  const qc = useQueryClient();
  const list = useServerFn(listMcpConnections);
  const save = useServerFn(saveMcpConnection);
  const del = useServerFn(deleteMcpConnection);
  const test = useServerFn(testMcpConnection);
  const [form, setForm] = useState<Form>(empty);
  const [results, setResults] = useState<Record<string, { status: string; message: string; tools: { name: string; description: string; blocked: boolean }[] }>>({});
  const [picks, setPicks] = useState<Record<string, string[]>>({});
  const setAllowed = useServerFn(setMcpAllowedTools);
  const allowM = useMutation({
    mutationFn: (v: { id: string; tools: string[] }) => setAllowed({ data: v }),
    onSuccess: refreshLater,
  });

  const q = useQuery({ queryKey: ["admin", "mcp-connections"], queryFn: () => list() });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "mcp-connections"] });
  function refreshLater() {
    refresh();
  }

  const saveM = useMutation({
    mutationFn: (f: Form) => save({ data: { ...f, token: f.token || undefined } }),
    onSuccess: () => {
      setForm(empty);
      refresh();
    },
  });
  const delM = useMutation({ mutationFn: (id: string) => del({ data: { id } }), onSuccess: refresh });
  const testM = useMutation({
    mutationFn: (id: string) => test({ data: { id } }),
    onSuccess: (r, id) => {
      setResults((s) => ({ ...s, [id]: r }));
      refresh();
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <h1 className="font-display text-4xl font-semibold">Conexiones MCP</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Servidores remotos con dirección y token fijo. El token se guarda de forma segura y no vuelve a mostrarse.
        </p>
        <div className="mt-6"><AdminNav /></div>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-base">{form.id ? "Editar conexión" : "Nueva conexión"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                saveM.mutate(form);
              }}
            >
              <div className="grid gap-1.5">
                <Label htmlFor="mcp-name">Nombre</Label>
                <Input id="mcp-name" placeholder="Bókun" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="mcp-url">Dirección del servidor</Label>
                <Input id="mcp-url" type="url" placeholder="https://…/mcp" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="mcp-token">Token Bearer</Label>
                <Input
                  id="mcp-token"
                  type="password"
                  autoComplete="off"
                  placeholder={form.id ? "Déjalo vacío para conservar el actual" : "Pega aquí el token"}
                  value={form.token}
                  onChange={(e) => setForm({ ...form, token: e.target.value })}
                  required={!form.id}
                />
                <p className="text-xs text-muted-foreground">Se envía como «Authorization: Bearer …». No escribas la palabra Bearer.</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="mcp-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
                <Label htmlFor="mcp-enabled">Activa</Label>
              </div>
              {saveM.error && <p role="alert" className="text-sm text-destructive">{(saveM.error as Error).message}</p>}
              <div className="flex gap-2">
                <Button type="submit" disabled={saveM.isPending}>{saveM.isPending ? "Guardando…" : "Guardar"}</Button>
                {form.id && <Button type="button" variant="outline" onClick={() => setForm(empty)}>Cancelar</Button>}
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="mt-8 space-y-4">
          {q.isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
          {q.error && <p role="alert" className="text-sm text-destructive">{(q.error as Error).message}</p>}
          {q.data?.items.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay conexiones.</p>}
          {(q.data?.items ?? []).map((c) => {
            const r = results[c.id];
            return (
              <Card key={c.id}>
                <CardContent className="space-y-3 pt-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-medium">
                        <Plug className="h-4 w-4" /> {c.name}
                        {!c.enabled && <Badge variant="secondary">Inactiva</Badge>}
                        {c.lastStatus === "ok" && <Badge>Conectada · {c.lastToolCount}</Badge>}
                        {c.lastStatus === "error" && <Badge variant="destructive">Error</Badge>}
                      </p>
                      <p className="break-all font-mono text-xs text-muted-foreground">{c.url}</p>
                      <p className="text-xs text-muted-foreground">Token {c.tokenHint}</p>
                      <p className="text-xs text-muted-foreground">
                        Herramientas aprobadas para Maya: {c.allowedTools.length ? c.allowedTools.join(", ") : "ninguna (Maya no usa esta conexión)"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={testM.isPending} onClick={() => testM.mutate(c.id)}>
                        <PlayCircle className="mr-1.5 h-3.5 w-3.5" /> Probar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setForm({ id: c.id, name: c.name, url: c.url, token: "", enabled: c.enabled })}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        aria-label="Eliminar"
                        onClick={() => confirm(`¿Eliminar «${c.name}»?`) && delM.mutate(c.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  {r && (
                    <div className="rounded-md bg-muted/40 p-3 text-xs">
                      <p className={r.status === "ok" ? "" : "text-destructive"}>{r.message}</p>
                      {r.tools.length > 0 && (() => {
                        const sel = picks[c.id] ?? c.allowedTools;
                        return (
                          <>
                            <p className="mt-2 text-muted-foreground">Marca solo las herramientas de consulta que Maya puede usar. Las de reserva, cobro, cancelación o modificación están bloqueadas.</p>
                            <ul className="mt-2 space-y-1.5">
                              {r.tools.map((t) => (
                                <li key={t.name} className="flex items-start gap-2">
                                  <Checkbox
                                    id={`${c.id}-${t.name}`}
                                    disabled={t.blocked}
                                    checked={!t.blocked && sel.includes(t.name)}
                                    onCheckedChange={(v) =>
                                      setPicks((p) => ({ ...p, [c.id]: v ? [...sel, t.name] : sel.filter((x) => x !== t.name) }))
                                    }
                                  />
                                  <label htmlFor={`${c.id}-${t.name}`}>
                                    <span className="font-mono">{t.name}</span>
                                    {t.blocked && <Badge variant="secondary" className="ml-1.5">Bloqueada</Badge>}
                                    <span className="text-muted-foreground"> — {t.description}</span>
                                  </label>
                                </li>
                              ))}
                            </ul>
                            <Button size="sm" className="mt-3" disabled={allowM.isPending} onClick={() => allowM.mutate({ id: c.id, tools: sel.filter((n) => r.tools.some((t) => t.name === n && !t.blocked)) })}>
                              Guardar herramientas aprobadas
                            </Button>
                            {allowM.error && <p role="alert" className="mt-1 text-destructive">{(allowM.error as Error).message}</p>}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
